import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Sparkles, Loader2, Coins } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { api, type User } from '@/lib/api'
import { openCheckout } from '@/lib/paddle'
import { captureEvent } from '@/lib/analytics'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const VITE_API_URL = import.meta.env.VITE_API_URL || ''

// 5¢ per credit. 1 credit = 1 summary; credits carry forward across cycles.
const CREDIT_UNIT_CENTS = 5
const CREDIT_PACKS = [100, 250, 500, 1000]

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: null,
    features: ['15 summaries / month', 'Unlimited channels', 'Summaries with timestamps'],
  },
  {
    id: 'plus',
    name: 'Plus',
    monthlyPrice: '7.99',
    yearlyPrice: '3.99',
    yearlyTotal: '47.99',
    popular: true,
    features: ['150 summaries / month', 'Unlimited channels', 'Daily niche brief by email'],
    monthlyPriceEnvKey: 'VITE_PADDLE_PRICE_PLUS_MONTHLY',
    yearlyPriceEnvKey: 'VITE_PADDLE_PRICE_PLUS_YEARLY',
  },
  {
    id: 'max',
    name: 'Max',
    monthlyPrice: '15.99',
    yearlyPrice: '7.99',
    yearlyTotal: '95.99',
    features: ['500 summaries / month', 'Everything in Plus', 'Niche-scoped search across all summaries'],
    monthlyPriceEnvKey: 'VITE_PADDLE_PRICE_MAX_MONTHLY',
    yearlyPriceEnvKey: 'VITE_PADDLE_PRICE_MAX_YEARLY',
  },
]

export function UpgradePage() {
  const { user, refresh } = useAuth()
  const navigate = useNavigate()
  const [annual, setAnnual] = useState(false)
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creditQty, setCreditQty] = useState(100)
  const [creditLoading, setCreditLoading] = useState(false)

  const handleUpgrade = async (planId: string) => {
    if (!user || planId === 'free') return

    const plan = PLANS.find(p => p.id === planId)
    if (!plan) return

    const priceEnvKey = annual ? plan.yearlyPriceEnvKey : plan.monthlyPriceEnvKey
    const priceId = priceEnvKey ? import.meta.env[priceEnvKey] : ''

    if (!priceId) {
      console.error('Missing Paddle price ID for', planId, annual ? 'yearly' : 'monthly')
      setError("Checkout isn't configured for this plan. Please contact support.")
      return
    }

    setError(null)
    setLoading(planId)

    try {
      // Create Paddle customer if needed
      const { customerId } = await api.createCheckout(priceId)

      openCheckout({
        priceId,
        userId: user.id,
        email: user.email,
        customerId,
        onSuccess: async () => {
          // Poll the server directly for the plan update — the webhook may take
          // a few seconds. We fetch fresh from /api/me rather than relying on
          // the useAuth state because setState inside refresh() doesn't update
          // the closure variable we captured above.
          let confirmedPlan: string | null = null
          for (let i = 0; i < 5; i++) {
            try {
              const { user: fresh } = await api.getMe()
              if (fresh.billing_plan !== 'free') {
                confirmedPlan = fresh.billing_plan
                break
              }
            } catch {
              // ignore and retry
            }
            await new Promise(r => setTimeout(r, 2000))
          }
          // Only fire on a *confirmed* plan flip (the webhook landed), so this
          // is a real conversion, not just "checkout overlay closed". Misses
          // users who close the tab during the poll — see note: airtight
          // revenue tracking would come from the Paddle webhook server-side.
          if (confirmedPlan) {
            captureEvent('plan_upgraded', {
              plan: confirmedPlan,
              interval: annual ? 'annual' : 'monthly',
              requested_plan: planId,
            })
          }
          // Sync the global auth state, then navigate.
          await refresh()
          navigate('/settings?upgraded=true')
        },
      })
    } catch (err) {
      console.error('Checkout error:', err)
      setError(err instanceof Error ? err.message : "Couldn't start checkout. Please try again.")
    } finally {
      setLoading(null)
    }
  }

  const handleBuyCredits = async () => {
    if (!user) return
    const qty = Math.max(100, Math.round(creditQty || 0))
    setError(null)
    setCreditLoading(true)
    try {
      const { customerId, priceId, quantity } = await api.createCreditsCheckout(qty)
      openCheckout({
        priceId,
        quantity,
        customerId,
        userId: user.id,
        email: user.email,
        onSuccess: async () => {
          captureEvent('credits_purchased', { quantity })
          await refresh()
          navigate('/settings')
        },
      })
    } catch (err) {
      console.error('Credits checkout error:', err)
      setError(err instanceof Error ? err.message : "Couldn't start checkout. Please try again.")
    } finally {
      setCreditLoading(false)
    }
  }

  const currentPlan = user?.billing_plan || 'free'
  const creditCostUsd = ((Math.max(100, creditQty || 0) * CREDIT_UNIT_CENTS) / 100).toFixed(2)

  return (
    <div className="py-8">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold">Upgrade your plan</h1>
        <p className="mt-2 text-muted-foreground">Get more summaries and unlock premium features.</p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-10">
        <span className={`text-sm font-medium ${!annual ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
        <button
          onClick={() => setAnnual(!annual)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${annual ? 'bg-primary' : 'bg-input'}`}
        >
          <span className={`pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg transition-transform ${annual ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
        <span className={`text-sm font-medium ${annual ? 'text-foreground' : 'text-muted-foreground'}`}>Annual</span>
        {annual && <Badge variant="success" className="text-xs">Save 50%</Badge>}
      </div>

      {error && (
        <div className="mx-auto mb-6 max-w-md rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        {PLANS.map((plan) => {
          const isCurrent = currentPlan === plan.id
          const isDowngrade = (currentPlan === 'max' && plan.id === 'plus') || (currentPlan !== 'free' && plan.id === 'free')
          const price = plan.monthlyPrice ? (annual ? plan.yearlyPrice : plan.monthlyPrice) : null

          return (
            <Card
              key={plan.id}
              className={`p-6 relative ${plan.popular ? 'border-2 border-primary' : ''}`}
            >
              {plan.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
                  Most popular
                </span>
              )}

              <h3 className="text-lg font-bold">{plan.name}</h3>

              {price ? (
                <div className="mt-3">
                  <span className="text-sm text-muted-foreground mr-0.5">$</span>
                  <span className="text-3xl font-extrabold">{price}</span>
                  <span className="text-sm text-muted-foreground ml-1">/ month</span>
                  {annual && plan.yearlyTotal && (
                    <p className="text-xs text-muted-foreground mt-0.5">Billed as ${plan.yearlyTotal}/year</p>
                  )}
                </div>
              ) : (
                <div className="mt-3">
                  <span className="text-sm text-muted-foreground mr-0.5">$</span>
                  <span className="text-3xl font-extrabold">0</span>
                  <span className="text-sm text-muted-foreground ml-1">/ forever</span>
                </div>
              )}

              <ul className="mt-5 space-y-2">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                {isCurrent ? (
                  <Button variant="outline" size="sm" className="w-full" disabled>
                    Current plan
                  </Button>
                ) : isDowngrade ? (
                  <Button variant="outline" size="sm" className="w-full" disabled>
                    {plan.id === 'free' ? 'Downgrade' : 'Contact us'}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="w-full gap-1.5"
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={loading !== null}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {loading === plan.id ? 'Opening checkout…' : `Upgrade to ${plan.name}`}
                  </Button>
                )}
              </div>
            </Card>
          )
        })}
      </div>

      {/* Rundown Credits — ad-hoc top-ups */}
      <div className="mt-10">
        <Card className="p-6">
          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Coins className="h-5 w-5 text-primary" />
              Rundown Credits
            </h3>
            <p className="text-sm text-muted-foreground">
              Hit your monthly limit? Top up with credits. <strong>1 credit = 1 summary</strong>, used automatically once your plan's allowance runs out. Unused credits roll over to the next cycle.
            </p>
            {user && user.credit_balance > 0 && (
              <p className="mt-1 text-sm font-medium text-foreground">
                You have <span className="text-primary">{user.credits_available}</span> credit{user.credits_available === 1 ? '' : 's'} available.
              </p>
            )}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {CREDIT_PACKS.map((pack) => (
              <button
                key={pack}
                type="button"
                onClick={() => setCreditQty(pack)}
                className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                  creditQty === pack ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:border-primary/50'
                }`}
              >
                {pack.toLocaleString()}
              </button>
            ))}
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={100}
                step={50}
                value={creditQty}
                onChange={(e) => setCreditQty(parseInt(e.target.value) || 0)}
                className="w-28 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                aria-label="Number of credits"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {Math.max(100, creditQty || 0).toLocaleString()} credits · <span className="font-semibold text-foreground">${creditCostUsd}</span>
              <span className="ml-1">(${(CREDIT_UNIT_CENTS / 100).toFixed(2)}/credit)</span>
            </p>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={handleBuyCredits}
              disabled={creditLoading || !user || (creditQty || 0) < 100}
            >
              {creditLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
              {creditLoading ? 'Opening checkout…' : 'Buy credits'}
            </Button>
          </div>
          {(creditQty || 0) < 100 && (
            <p className="mt-2 text-xs text-destructive">Minimum purchase is 100 credits.</p>
          )}
        </Card>
      </div>
    </div>
  )
}
